import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CloseButton } from "@/components/atoms";
import type { ModalWindowProps } from "@/types/modal";
import { DEFAULT_MODAL_OPTIONS } from "@/types/modal";
import { LoadingContainer, ErrorContainer } from "@/components/molecules";
import { breakpoints } from "@/theme/muiTheme";

/**
 * Convert breakpoint values to pixels
 */
const convertMaxWidthToPx = (maxWidth: string | false): string => {
  if (maxWidth === false) {
    return "100%";
  }

  const pxValue =
    breakpoints.values[maxWidth as keyof typeof breakpoints.values];
  return pxValue ? `${pxValue}px` : "100%";
};

/**
 * Enhanced Modal Window Component
 *
 * Optimized for factory pattern usage with improved performance and modal type awareness.
 *
 * Enhancements:
 * - Modal type identification for customization
 * - Optimized z-index management for modal stacking
 * - Enhanced performance with React.memo
 * - Improved factory pattern integration
 * - Better state management coordination
 */
const ModalWindowComponent: React.FC<ModalWindowProps> = ({
  open,
  onClose,
  title,
  children,
  options = {},
  loading = false,
  error = null,
  className,
  "data-testid": dataTestId,
}) => {
  const theme = useTheme();

  // Responsive design
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  // Merge options with defaults - optimized for factory pattern
  const mergedOptions = useMemo(
    () => ({
      ...DEFAULT_MODAL_OPTIONS,
      ...options,
      // Enhanced z-index handling for modal stacking
      zIndex: options.zIndex || DEFAULT_MODAL_OPTIONS.zIndex,
    }),
    [options],
  );

  // Extract modal type from options for potential customization
  const modalType = useMemo(() => {
    // Could be passed from factory pattern for type-specific customization
    return (options as any).modalType || "default";
  }, [options]);

  // Determine final anchor based on prop and responsive logic
  const finalAnchor = useMemo(() => {
    // If mobile and fullscreen option is enabled, force bottom anchor
    if (isMobile && mergedOptions.fullscreenOnMobile) {
      return "bottom";
    }
    // Otherwise use the provided anchor prop
    return mergedOptions.anchor;
  }, [isMobile, mergedOptions.fullscreenOnMobile, mergedOptions.anchor]);

  // Swipe handling state and refs
  const drawerRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<{
    x: number;
    y: number;
    time: number;
  } | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isReturning, setIsReturning] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Determine if we're in fullscreen mode
  const isFullscreen = isMobile && mergedOptions.fullscreenOnMobile;

  // Handle keyboard events - optimized with useCallback
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && mergedOptions.closeOnEscape && open) {
        onClose();
      }
    },
    [mergedOptions.closeOnEscape, open, onClose],
  );

  // Add keyboard listeners
  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  // Reset all swipe states when modal closes
  useEffect(() => {
    if (!open) {
      setDragOffset(0);
      setIsReturning(false);
      setIsClosing(false);
      setTouchStart(null);
    }
  }, [open]);

  // Handle backdrop click - optimized with useCallback
  const handleBackdropClick = useCallback(() => {
    if (mergedOptions.closeOnOverlayClick) {
      onClose();
    }
  }, [mergedOptions.closeOnOverlayClick, onClose]);

  // Animate return to original position
  const animateReturn = useCallback(() => {
    if (isReturning || isClosing) return;

    setIsReturning(true);
    const startOffset = dragOffset;
    const startTime = Date.now();
    const duration = 300; // 300ms animation

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const currentOffset = startOffset * (1 - easeOut);
      setDragOffset(currentOffset);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete
        setDragOffset(0);
        setIsReturning(false);
        setTouchStart(null);
      }
    };

    requestAnimationFrame(animate);
  }, [dragOffset, isReturning, isClosing]);

  // Animate closing - move modal down and then close
  const animateClose = useCallback(() => {
    if (isClosing) return;

    setIsClosing(true);
    const startOffset = dragOffset;
    const startTime = Date.now();
    const duration = 250; // 250ms animation (slightly faster for closing)

    // Calculate target offset (move modal completely off screen)
    const targetOffset = window.innerHeight;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth closing animation
      const easeIn = Math.pow(progress, 2);

      const currentOffset = startOffset + (targetOffset - startOffset) * easeIn;
      setDragOffset(currentOffset);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete - now close the modal
        onClose();
        // Note: States will be reset when modal closes via useEffect
      }
    };

    requestAnimationFrame(animate);
  }, [dragOffset, isClosing, onClose]);

  // Touch/Swipe handlers for mobile - optimized for performance
  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      if (!isFullscreen || !open || isReturning || isClosing) return;

      const modalContent = document.getElementById("modal-content");
      if (!modalContent) return;

      // Only start swipe if modal content is at the top
      if (modalContent.scrollTop !== 0) return;

      const touch = event.touches[0];
      setTouchStart({
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      });
      setDragOffset(0);
    },
    [isFullscreen, open, isReturning, isClosing],
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      if (!isFullscreen || !open || !touchStart || isReturning || isClosing)
        return;

      const modalContent = document.getElementById("modal-content");
      if (!modalContent) return;

      const touch = event.touches[0];
      const deltaY = touch.clientY - touchStart.y;
      const deltaX = Math.abs(touch.clientX - touchStart.x);

      // Only handle vertical swipes (more vertical than horizontal movement)
      if (deltaX > Math.abs(deltaY)) return;

      // Check if we're still at the top OR if we're already dragging
      if (modalContent.scrollTop === 0 || dragOffset > 0) {
        // Only allow dragging down
        if (deltaY > 0) {
          // No resistance - 1:1 movement ratio
          setDragOffset(deltaY);

          // Prevent default scrolling when dragging
          event.preventDefault();
        }
      }
    },
    [isFullscreen, open, touchStart, dragOffset, isReturning, isClosing],
  );

  const handleTouchEnd = useCallback(() => {
    if (!isFullscreen || !open || !touchStart || isReturning || isClosing)
      return;

    const currentTime = Date.now();
    const timeDiff = currentTime - touchStart.time;

    // Minimum movement threshold - if drag is too small, just reset
    const minMovementThreshold = 10; // pixels
    if (dragOffset < minMovementThreshold) {
      setTouchStart(null);
      setDragOffset(0);
      return;
    }

    // Calculate velocity only if timeDiff is reasonable (avoid division by very small numbers)
    const velocity = timeDiff > 50 ? dragOffset / timeDiff : 0; // pixels per millisecond

    // Fast swipe detection (minimum velocity threshold)
    const fastSwipeThreshold = 0.8; // pixels per millisecond (increased)
    const normalThreshold = 80; // pixels (increased)

    const shouldClose =
      (velocity > fastSwipeThreshold && dragOffset > 30) || // fast swipe needs minimum distance too
      dragOffset > normalThreshold;

    // Debug logging (can be removed in production)
    // console.log("Touch end:", {
    //   dragOffset,
    //   timeDiff,
    //   velocity,
    //   shouldClose,
    //   fastSwipeThreshold,
    //   normalThreshold,
    // });

    if (shouldClose) {
      // Animate closing - move modal down and then close
      animateClose();
    } else {
      // Return to original position with animation
      animateReturn();
    }
  }, [
    isFullscreen,
    open,
    touchStart,
    dragOffset,
    isReturning,
    isClosing,
    animateReturn,
    animateClose,
  ]);

  // Add touch listeners for swipe - now targeting modal-content
  useEffect(() => {
    if (open && isFullscreen) {
      const modalContent = document.getElementById("modal-content");
      if (!modalContent) return;

      modalContent.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      modalContent.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      modalContent.addEventListener("touchend", handleTouchEnd, {
        passive: true,
      });

      return () => {
        modalContent.removeEventListener("touchstart", handleTouchStart);
        modalContent.removeEventListener("touchmove", handleTouchMove);
        modalContent.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [open, isFullscreen, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Handle close button click - optimized with useCallback
  const handleCloseClick = useCallback(() => {
    onClose();
  }, [onClose]);

  // Calculate drawer width based on maxWidth option - optimized
  const drawerWidth = useMemo(() => {
    if (isFullscreen) {
      return "100%";
    }

    return convertMaxWidthToPx(mergedOptions.maxWidth);
  }, [isFullscreen, mergedOptions.maxWidth]);

  // Common paper styles for all states - optimized with useMemo
  const paperStyles = useMemo(() => {
    const sx = {
      width: { xs: "100%", lg: drawerWidth },
      maxWidth: "100vw",
      backgroundColor: "transparent",
      backgroundImage: "none",
      boxShadow: "none",
      minHeight: { xs: "100vh", lg: "80%" },
      // position: "relative",
      p: finalAnchor === "bottom" ? { xs: 0, lg: 1 } : 1,
      pt: finalAnchor === "bottom" ? { xs: 5 } : 1,
      transition: "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
      // Removed opacity change during drag
    };

    if (finalAnchor === "bottom") {
      return {
        ...sx,
        left: "50%",
        transform: "translateX(-50%) !important",
      };
    }

    return sx;
  }, [finalAnchor, drawerWidth]);

  // Enhanced content rendering with modal type awareness
  const renderContent = useCallback(() => {
    const baseBoxStyles = {
      backgroundColor: "background.paper",
      borderRadius:
        finalAnchor === "bottom" ? { xs: "30px 30px 0 0", lg: "24px" } : "24px",
      display: "flex",
      flex: 1,
      overflow: "hidden",
      // Add transform for drag offset
      transform:
        isFullscreen && dragOffset > 0 ? `translateY(${dragOffset}px)` : "none",
      transition:
        dragOffset > 0 && !isReturning && !isClosing
          ? "none"
          : "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
    };

    if (loading) {
      return (
        <Box
          sx={{
            ...baseBoxStyles,
            minHeight: finalAnchor === "bottom" ? "300px" : "100%",
            transition:
              "min-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <LoadingContainer
            message="Загружается..."
            loading={true}
            variant="modal"
          />
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          sx={{
            ...baseBoxStyles,
            minHeight: finalAnchor === "bottom" ? "50vh" : "100%",
            position: "relative",
          }}
        >
          <Box sx={{ p: 2, flex: 1 }}>
            <ErrorContainer
              error={error}
              message="Произошла ошибка при загрузке"
              showRetry={false}
            />
          </Box>
          {mergedOptions.showCloseButton && (
            <Box
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseButton onClick={handleCloseClick} size="small" />
            </Box>
          )}
        </Box>
      );
    }

    // Normal content state with enhanced modal type support
    return (
      <>
        {/* Drag indicator for bottom drawer */}
        {finalAnchor === "bottom" && (
          <Box
            sx={{
              position: "absolute",
              top: -16,
              zIndex: 1000,
              left: "50%",
              transform: "translateX(-50%)",
              width: "80px",
              height: "5px",
              borderRadius: "10px",
              backgroundColor: "white",
              opacity: 0.5,
              transition: "opacity 0.2s ease",
              cursor: "grab",
              "&:before": {
                content: "''",
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "background.paper",
                opacity: 0.5,
                width: 80,
                height: 5,
                borderRadius: "3px",
              },
            }}
          />
        )}

        <Box
          sx={{
            ...baseBoxStyles,
            minHeight: finalAnchor === "bottom" ? "50vh" : "100%",
            position: "relative",
            transition:
              "min-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            // Modal type-specific customizations could be added here
            ...(modalType === "product" && {
              maxWidth: { xs: "100%", xl: "900px", x2l: "1200px" },
            }),
            ...(modalType === "order" && {
              maxWidth: { xs: "100%", xl: "600px" },
            }),
          }}
        >
          {mergedOptions.showCloseButton && (
            <CloseButton onClick={handleCloseClick} />
          )}

          <Box
            sx={{
              flex: 1,
              maxHeight: "100%",
              overflow: "hidden",
              display: "flex",
              borderRadius:
                finalAnchor === "bottom"
                  ? { xs: "30px 30px 0 0", x2l: "20px" }
                  : "20px",
            }}
          >
            {/* Modal Content - Enhanced for factory pattern */}
            <Box
              id="modal-content"
              data-modal-type={modalType}
              sx={{
                p: 0,
                flex: 1,
                overflow: "auto",
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>
      </>
    );
  }, [
    finalAnchor,
    loading,
    error,
    mergedOptions.showCloseButton,
    handleCloseClick,
    modalType,
    children,
    isFullscreen,
    dragOffset,
    isReturning,
    isClosing,
  ]);

  // Enhanced Drawer component with factory pattern optimizations
  return (
    <Drawer
      ref={drawerRef}
      anchor={finalAnchor}
      open={open}
      onClose={handleBackdropClick}
      className={className}
      data-testid={dataTestId}
      data-modal-type={modalType} // Add modal type for testing/debugging
      sx={{
        zIndex: mergedOptions.zIndex,
        "& .MuiDrawer-paper": paperStyles,
        // Enhanced performance optimizations
        "& .MuiBackdrop-root": {
          transition: "opacity 0.225s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      }}
      aria-labelledby={title ? "modal-title" : undefined}
      aria-describedby="modal-content"
      // Enhanced accessibility for different modal types
      role="dialog"
      aria-modal="true"
    >
      {renderContent()}
    </Drawer>
  );
};

// Enhanced memoization for factory pattern performance
export const ModalWindow = React.memo(
  ModalWindowComponent,
  (prevProps, nextProps) => {
    // Custom comparison for optimal re-rendering
    return (
      prevProps.open === nextProps.open &&
      prevProps.loading === nextProps.loading &&
      prevProps.error === nextProps.error &&
      prevProps.children === nextProps.children &&
      JSON.stringify(prevProps.options) === JSON.stringify(nextProps.options)
    );
  },
);

// Set display name for debugging
ModalWindow.displayName = "ModalWindow";
